/**
 * useShopRealtime — opens a Server-Sent Events connection to the shop event stream
 * so the open dashboard reacts instantly when a parcel arrives or a return is queued.
 *
 * EventSource auto-reconnects with exponential backoff. We pass the shop's JWT as a
 * query-param (?token=...) because EventSource cannot send Authorization headers.
 */
import { useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useShopRealtime(token, onEvent) {
  const [connected, setConnected] = useState(false);
  const esRef = useRef(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    const open = () => {
      if (cancelled) return;
      try {
        const url = `${API}/pakkeshop/events?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        esRef.current = es;
        es.addEventListener("ready", () => setConnected(true));
        es.addEventListener("open", () => setConnected(true));
        es.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (onEventRef.current) onEventRef.current(data);
          } catch (e) { /* ignore malformed */ }
        };
        es.onerror = () => {
          setConnected(false);
          // EventSource auto-reconnects, but if it transitioned to CLOSED we re-open manually.
          if (es.readyState === 2 && !cancelled) {
            try { es.close(); } catch { /* ignore */ }
            setTimeout(open, 3000);
          }
        };
      } catch (e) {
        console.warn("shop SSE open failed:", e?.message);
      }
    };

    open();
    return () => {
      cancelled = true;
      try { esRef.current?.close(); } catch { /* ignore */ }
      esRef.current = null;
      setConnected(false);
    };
  }, [token]);

  return { connected };
}
